/**
 * Sanitizes HTML content to prevent XSS attacks
 * Allows safe HTML tags and attributes while stripping dangerous content
 * 
 * Note: This function should only be called on the client side to avoid
 * jsdom dependency issues in Next.js 16/Turbopack. For server components,
 * use a client component wrapper that calls this in useEffect.
 */
export function sanitizeHtml(html: string): string {
  // This should only run on the client
  if (typeof window === 'undefined') {
    // Server-side: basic regex-based sanitization to remove script tags
    // This is a minimal safety measure; full sanitization happens on client
    return html
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
      .replace(/javascript:/gi, '');
  }

  // Client-side: use DOMPurify for full sanitization
   
  const DOMPurify = require('isomorphic-dompurify');
  
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'div', 'p', 'span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'a', 'img', 'button', 'ul', 'ol', 'li', 'br', 'hr',
      'strong', 'em', 'b', 'i', 'u', 'code', 'pre', 'blockquote',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'section', 'article', 'header', 'footer', 'nav', 'main',
      // allow designers to bring their own CSS via link tags
      'link'
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'class', 'id', 'target',
      'width', 'height', 'style', 'role', 'aria-label',
      // for <link rel="stylesheet" href="/...">
      'rel', 'type'
    ],
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    KEEP_CONTENT: true,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_TRUSTED_TYPE: false,
  });
}

